/**
 * Accounts API service.
 *
 * Mirrors the People service shape (list, metrics, detail, CSV export).
 * Accounts are corporate domain rollups across people in an organization.
 */

import apiClient from './client';
import type { ApiResponse } from './client';

// ─── Types ───────────────────────────────────────────────────────────────────

export type HealthBand = 'hot' | 'warm' | 'neutral';
export type EnrichmentStatus = 'pending' | 'enriched' | 'failed';
export type AccountSortBy =
  | 'last_seen_at'
  | 'total_users'
  | 'total_sessions'
  | 'high_intent_users'
  | 'health_band';
export type SortDirection = 'asc' | 'desc';

export interface AccountSummary {
  account_domain: string;
  company_name: string | null;
  total_users: number;
  total_sessions: number;
  booked_sessions: number;
  high_intent_users: number;
  qualified_users: number;
  last_seen_at: string | null;
  enrichment_status: EnrichmentStatus;
  health_band: HealthBand;
}

export interface AccountDetail extends AccountSummary {
  summary_markdown: string | null;
  total_time_spent_seconds: number;
}

export interface AccountUser {
  email: string;
  name: string | null;
  total_sessions: number;
  buying_intent: string | null;
  qualification_status: string | null;
  last_seen_at: string | null;
  summary_markdown: string | null;
}

export interface AccountsListResponse {
  accounts: AccountSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface AccountsMetricsResponse {
  total_accounts: number;
  avg_users_per_account: number;
  total_high_intent_users: number;
  total_qualified_users: number;
  total_accounts_change?: number | null;
  avg_users_per_account_change?: number | null;
  total_high_intent_users_change?: number | null;
  total_qualified_users_change?: number | null;
}

export interface AccountUsersResponse {
  users: AccountUser[];
  total: number;
  limit: number;
  offset: number;
}

export interface AccountDetailResponse {
  account: AccountDetail;
  users: AccountUser[];
  total_users: number;
}

export interface AccountsFilters {
  search?: string;
  health?: string; // comma-separated HealthBand values
  min_users?: number;
  date_from?: string;
  date_to?: string;
  sort_by?: AccountSortBy;
  sort_direction?: SortDirection;
  limit?: number;
  offset?: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

class AccountsService {
  async getAccountsList(
    organizationId: string,
    filters?: AccountsFilters,
  ): Promise<ApiResponse<AccountsListResponse>> {
    const query: Record<string, string> = {};
    if (filters?.search) query.search = filters.search;
    if (filters?.health) query.health = filters.health;
    if (filters?.min_users !== undefined) query.min_users = String(filters.min_users);
    if (filters?.date_from) query.date_from = filters.date_from;
    if (filters?.date_to) query.date_to = filters.date_to;
    if (filters?.sort_by) query.sort_by = filters.sort_by;
    if (filters?.sort_direction) query.sort_direction = filters.sort_direction;
    if (filters?.limit !== undefined) query.limit = String(filters.limit);
    if (filters?.offset !== undefined) query.offset = String(filters.offset);

    return await apiClient.get<AccountsListResponse>(
      `/api/accounts/organizations/${organizationId}/list`,
      query,
    );
  }

  async getAccountsMetrics(
    organizationId: string,
    filters?: { date_from?: string; date_to?: string },
  ): Promise<ApiResponse<AccountsMetricsResponse>> {
    const query: Record<string, string> = {};
    if (filters?.date_from) query.date_from = filters.date_from;
    if (filters?.date_to) query.date_to = filters.date_to;

    return await apiClient.get<AccountsMetricsResponse>(
      `/api/accounts/organizations/${organizationId}/metrics`,
      query,
    );
  }

  async getAccountDetail(
    organizationId: string,
    accountDomain: string,
    options?: { users_limit?: number; users_offset?: number },
  ): Promise<ApiResponse<AccountDetailResponse>> {
    const query: Record<string, string> = { account_domain: accountDomain };
    if (options?.users_limit !== undefined) query.users_limit = String(options.users_limit);
    if (options?.users_offset !== undefined) query.users_offset = String(options.users_offset);

    return await apiClient.get<AccountDetailResponse>(
      `/api/accounts/organizations/${organizationId}/account`,
      query,
    );
  }

  async getAccountUsers(
    organizationId: string,
    accountDomain: string,
    options?: { limit?: number; offset?: number },
  ): Promise<ApiResponse<AccountUsersResponse>> {
    const query: Record<string, string> = { account_domain: accountDomain };
    if (options?.limit !== undefined) query.limit = String(options.limit);
    if (options?.offset !== undefined) query.offset = String(options.offset);

    return await apiClient.get<AccountUsersResponse>(
      `/api/accounts/organizations/${organizationId}/account/users`,
      query,
    );
  }

  async exportAccountsCsv(
    organizationId: string,
    filters?: AccountsFilters,
  ): Promise<void> {
    const url = new URL(
      `${apiClient.getBaseUrl()}/api/accounts/organizations/${organizationId}/export-csv`,
    );

    if (filters?.search) url.searchParams.set('search', filters.search);
    if (filters?.health) url.searchParams.set('health', filters.health);
    if (filters?.min_users !== undefined) url.searchParams.set('min_users', String(filters.min_users));
    if (filters?.date_from) url.searchParams.set('date_from', filters.date_from);
    if (filters?.date_to) url.searchParams.set('date_to', filters.date_to);
    if (filters?.sort_by) url.searchParams.set('sort_by', filters.sort_by);
    if (filters?.sort_direction) url.searchParams.set('sort_direction', filters.sort_direction);

    const token = apiClient.getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(url.toString(), { method: 'GET', headers });
    if (!response.ok) throw new Error('Failed to export accounts CSV');

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = 'accounts_export.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  }
}

const accountsService = new AccountsService();
export default accountsService;
